import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Loader2, TrendingUp, Users, ShoppingCart, DollarSign, ArrowUpRight, ArrowDownRight, Calendar, Mail, Check } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const fmt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => '₺' + Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

const MONTHS_TR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

export default function DailyTraffic() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!brandId) return;
    setLoading(true);

    const year = new Date().getFullYear();
    const url = `${API_BASE}/api/google-ads/ga-summary-daily?dateRange=custom&startDate=${year}-01-01&endDate=${year}-12-31`;

    fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
    })
      .then(r => r.json())
      .then(data => {
        const sorted = (data.rows || []).sort((a, b) => a.date.localeCompare(b.date));
        setRows(sorted);
      })
      .catch(err => console.error('DailyTraffic fetch error:', err))
      .finally(() => setLoading(false));
  }, [brandId]);

  const filtered = useMemo(() => {
    if (selectedMonth === 'all') return rows;
    return rows.filter(r => {
      const m = new Date(r.date).getMonth();
      return m === Number(selectedMonth);
    });
  }, [rows, selectedMonth]);

  const totals = useMemo(() => {
    return filtered.reduce((acc, r) => {
      acc.sessions += r.sessions;
      acc.users += r.users;
      acc.transactions += r.transactions;
      acc.revenue += r.revenue;
      return acc;
    }, { sessions: 0, users: 0, transactions: 0, revenue: 0 });
  }, [filtered]);

  const avgCr = totals.sessions > 0 ? ((totals.transactions / totals.sessions) * 100) : 0;
  const avgAov = totals.transactions > 0 ? totals.revenue / totals.transactions : 0;
  const daysCount = filtered.length;

  const availableMonths = useMemo(() => {
    const set = new Set();
    rows.forEach(r => set.add(new Date(r.date).getMonth()));
    return Array.from(set).sort((a, b) => a - b);
  }, [rows]);

  const handleSendEmail = async () => {
    if (!brandId || sending) return;
    setSending(true);
    setSent(false);
    try {
      const res = await fetch(`${API_BASE}/api/email/send-daily-traffic`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ brandId, email: 'marcom@gurgencler.com', month: selectedMonth }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
        setTimeout(() => setSent(false), 4000);
      } else {
        alert('Mail gönderilemedi: ' + (data.error || 'Bilinmeyen hata'));
      }
    } catch (err) {
      alert('Mail gönderilemedi: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daily GA4 Traffic & Transactions</h1>
          <p className="text-sm text-gray-500 mt-1">{new Date().getFullYear()} · Günlük Sessions, Transactions, Revenue</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="all">Tüm Yıl ({new Date().getFullYear()})</option>
              {availableMonths.map(m => (
                <option key={m} value={m}>{MONTHS_TR[m]}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleSendEmail}
            disabled={sending || filtered.length === 0}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              sent
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
            }`}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : sent ? <Check className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
            {sending ? 'Gönderiliyor...' : sent ? 'Gönderildi!' : 'Mail Gönder'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <SummaryCard icon={Users} label="Sessions" value={fmt(totals.sessions)} color="blue" />
        <SummaryCard icon={Users} label="Users" value={fmt(totals.users)} color="indigo" />
        <SummaryCard icon={ShoppingCart} label="Transactions" value={fmt(totals.transactions)} color="emerald" />
        <SummaryCard icon={DollarSign} label="Revenue" value={fmtMoney(totals.revenue)} color="green" />
        <SummaryCard icon={TrendingUp} label="CR %" value={`%${avgCr.toFixed(2)}`} color="amber" />
        <SummaryCard icon={DollarSign} label="AOV" value={fmtMoney(avgAov)} color="violet" />
      </div>

      {/* Daily avg */}
      {daysCount > 0 && (
        <div className="bg-gray-50 rounded-xl border p-4 flex flex-wrap gap-6 text-sm">
          <span className="text-gray-500">Günlük Ort:</span>
          <span><strong>{fmt(Math.round(totals.sessions / daysCount))}</strong> sessions</span>
          <span><strong>{fmt(Math.round(totals.transactions / daysCount))}</strong> transactions</span>
          <span><strong>{fmtMoney(Math.round(totals.revenue / daysCount))}</strong> revenue</span>
          <span className="text-gray-400">({daysCount} gün)</span>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Tarih</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Gün</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">Sessions</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">Users</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">Transactions</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">Revenue</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">CR %</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">AOV</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600">Δ Sessions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => {
                const prev = i > 0 ? filtered[i - 1] : null;
                const sessionsDelta = prev ? row.sessions - prev.sessions : null;
                const cr = row.sessions > 0 ? ((row.transactions / row.sessions) * 100) : 0;
                const aov = row.transactions > 0 ? row.revenue / row.transactions : 0;
                const isWeekend = row.dow_label === 'Sat' || row.dow_label === 'Sun'
                  || row.iso_dow === 6 || row.iso_dow === 7;

                return (
                  <tr
                    key={row.date}
                    className={`border-b border-gray-100 hover:bg-blue-50/30 transition-colors ${isWeekend ? 'bg-gray-50/50' : ''}`}
                  >
                    <td className="py-2.5 px-4 font-mono text-xs text-gray-900">{String(row.date).slice(0, 10)}</td>
                    <td className="py-2.5 px-4 text-xs text-gray-500">{row.dow_label || ''}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-medium">{fmt(row.sessions)}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-gray-600">{fmt(row.users)}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-medium text-emerald-700">{fmt(row.transactions)}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-semibold text-green-700">{fmtMoney(row.revenue)}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-xs text-gray-600">%{cr.toFixed(2)}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-xs text-gray-600">{row.transactions > 0 ? fmtMoney(aov) : '-'}</td>
                    <td className="py-2.5 px-4 text-center">
                      {sessionsDelta !== null ? (
                        <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${sessionsDelta > 0 ? 'text-emerald-600' : sessionsDelta < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                          {sessionsDelta > 0 ? <ArrowUpRight className="w-3 h-3" /> : sessionsDelta < 0 ? <ArrowDownRight className="w-3 h-3" /> : null}
                          {sessionsDelta > 0 ? '+' : ''}{fmt(sessionsDelta)}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">Veri bulunamadı</td>
                </tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-gray-900 text-white font-semibold">
                  <td className="py-3 px-4" colSpan={2}>TOPLAM ({daysCount} gün)</td>
                  <td className="py-3 px-4 text-right font-mono">{fmt(totals.sessions)}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmt(totals.users)}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmt(totals.transactions)}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtMoney(totals.revenue)}</td>
                  <td className="py-3 px-4 text-right font-mono">%{avgCr.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-mono">{totals.transactions > 0 ? fmtMoney(avgAov) : '-'}</td>
                  <td className="py-3 px-4"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    violet: 'bg-violet-50 text-violet-600',
  };
  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}
